import React, { useCallback, useEffect, useMemo, useState } from "react"
import { Project } from "../../dto/project/Project"
import { useProject } from "../../hooks/projects/use-projects"

export const ProjectCountersContext = React.createContext<ProjectCountersStore>({} as ProjectCountersStore)

interface IProps {
  project: Project | undefined
  children?: React.ReactNode
}

export function ProjectCountersContextProvider({ project, children }: Readonly<IProps>): React.JSX.Element {
  const { countBimModel, countRseeDocuments, countVariants } = useProject()

  const [rseeDocumentNumber, setRseeDocumentNumber] = useState<number>(0)
  const [isRseeDocumentNumberReady, setIsRseeDocumentNumberReady] = useState<boolean>(false)
  const [variantNumber, setVariantNumber] = useState<number>(0)
  const [isVariantNumberReady, setIsVariantNumberReady] = useState<boolean>(false)
  const [bimModelNumber, setBimModelNumber] = useState<number>(0)
  const [isBimModelNumberReady, setIsBimModelNumberReady] = useState<boolean>(false)

  const fetchRseeDocumentCount = useCallback(() => {
    if (project?.id) {
      setIsRseeDocumentNumberReady(false)
      countRseeDocuments(project.id)
        .then(setRseeDocumentNumber)
        .finally(() => setIsRseeDocumentNumberReady(true))
    } else {
      setRseeDocumentNumber(0)
      setIsRseeDocumentNumberReady(false)
    }
  }, [project?.id, countRseeDocuments])

  const fetchVariantCount = useCallback(() => {
    if (project?.id) {
      setIsVariantNumberReady(false)
      countVariants(project.id)
        .then(setVariantNumber)
        .finally(() => setIsVariantNumberReady(true))
    } else {
      setVariantNumber(0)
      setIsVariantNumberReady(false)
    }
  }, [project?.id, countVariants])

  const fetchBimModelCount = useCallback(() => {
    if (project?.id) {
      setIsBimModelNumberReady(false)
      countBimModel(project.id)
        .then(setBimModelNumber)
        .finally(() => setIsBimModelNumberReady(true))
    } else {
      setBimModelNumber(0)
      setIsBimModelNumberReady(false)
    }
  }, [project?.id, countBimModel])

  const refreshAllCounts = useCallback(() => {
    fetchRseeDocumentCount()
    fetchVariantCount()
    fetchBimModelCount()
  }, [fetchRseeDocumentCount, fetchVariantCount, fetchBimModelCount])

  useEffect(() => {
    refreshAllCounts()
  }, [refreshAllCounts])

  const projectCountersStore: ProjectCountersStore = useMemo(
    () => ({
      rseeDocumentNumber,
      isRseeDocumentNumberReady,
      variantNumber,
      isVariantNumberReady,
      bimModelNumber,
      isBimModelNumberReady,
      refreshAllCounts,
      fetchBimModelCount,
    }),
    [
      rseeDocumentNumber,
      isRseeDocumentNumberReady,
      variantNumber,
      isVariantNumberReady,
      bimModelNumber,
      isBimModelNumberReady,
      refreshAllCounts,
      fetchBimModelCount,
    ]
  )

  return <ProjectCountersContext.Provider value={projectCountersStore}>{children}</ProjectCountersContext.Provider>
}

export interface ProjectCountersStore {
  rseeDocumentNumber: number
  isRseeDocumentNumberReady: boolean
  variantNumber: number
  isVariantNumberReady: boolean
  bimModelNumber: number
  isBimModelNumberReady: boolean
  refreshAllCounts(): void
  fetchBimModelCount(): void
}
